import {
  AllUsersData,
  DeleteUserData,
  DeleteUserVars,
  UpdateUserNamesData,
  UpdateUserNamesVars,
  VerifyOrUnverifyEmailManaullyData,
  VerifyOrUnverifyEmailManaullyVars
} from '../gql-types';
import {
  ApolloClient,
  DataProxy,
  ExecutionPatchResult,
  NormalizedCacheObject,
  useMutation
} from '@apollo/client';
import { useEffect, useState } from 'react';

import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import InlineTextEditCallback from '../../shared/models/InlineTextEditCallback';
import MembershipGraphQL from '../gql';
import UpdateUserNamesRequest from '../models/UpdateUserNamesRequest';
import User from '../models/User';
import { cloneDeep } from 'lodash';

let membershipApolloClient: ApolloClient<NormalizedCacheObject> = null;

interface UpdateUserNamesContext {
  request: UpdateUserNamesRequest;
  callback: InlineTextEditCallback<User>;
}

function useUserEdit() {
  if (!membershipApolloClient) {
    membershipApolloClient = ApolloClientService.create(Config.MEMBERSHIP_GQL);
  }

  const [updateUserNamesContext, setUpdateUserNamesContext] =
    useState<UpdateUserNamesContext>();
  const [
    updateUserNamesErrorAcknowledged,
    setUpdateUserNamesErrorAcknowledged
  ] = useState(false);
  const [deleteErrorAcknowledged, setDeleteErrorAcknowledged] = useState(false);

  const [
    updateUserNames,
    {
      called: updateUserNamesCalled,
      loading: updateUserNamesLoading,
      error: updateUserNamesError,
      data: updateUserNamesData
    }
  ] = useMutation<UpdateUserNamesData, UpdateUserNamesVars>(
    MembershipGraphQL.Mutation.UPDATE_USER_NAMES,
    {
      client: membershipApolloClient,
      refetchQueries: ({ data }: ExecutionPatchResult<UpdateUserNamesData>) => {
        return [
          {
            query: MembershipGraphQL.Query.USER_BY_KEY,
            variables: { userkey: data.updateUserNames.userkey }
          }
        ];
      },
      awaitRefetchQueries: true,
      update: (
        cache: DataProxy,
        { data }: ExecutionPatchResult<UpdateUserNamesData>
      ) => {
        let users: AllUsersData;
        try {
          users = cache.readQuery<AllUsersData>({
            query: MembershipGraphQL.Query.ALL_USERS
          });
        } catch (error) {
          // NOP - cache miss ok
        }
        if (!users) {
          return;
        }
        const usersClone = cloneDeep(users.allUsers);
        const user = usersClone.find(
          (u) => u.userkey === data.updateUserNames.userkey
        );
        const { firstname, lastname } = data.updateUserNames;
        user.firstname = firstname;
        user.lastname = lastname;
        cache.writeQuery<AllUsersData>({
          query: MembershipGraphQL.Query.ALL_USERS,
          data: { allUsers: usersClone }
        });
      }
    }
  );

  if (updateUserNamesLoading && updateUserNamesErrorAcknowledged) {
    setUpdateUserNamesErrorAcknowledged(false);
  }

  function acknowledgeUpdateUserNamesError() {
    setUpdateUserNamesErrorAcknowledged(true);
  }

  useEffect(() => {
    if (updateUserNamesContext?.callback) {
      updateUserNamesContext.callback(updateUserNamesLoading, false, undefined);
    }
  }, [updateUserNamesLoading]);

  useEffect(() => {
    if (updateUserNamesContext?.callback && updateUserNamesError) {
      updateUserNamesContext.callback(false, !!updateUserNamesError, undefined);
      setUpdateUserNamesContext(undefined);
    }
  }, [updateUserNamesError]);

  useEffect(() => {
    if (updateUserNamesContext?.callback && updateUserNamesData) {
      updateUserNamesContext.callback(
        false,
        false,
        updateUserNamesData.updateUserNames
      );
      setUpdateUserNamesContext(undefined);
    }
  }, [updateUserNamesData]);

  useEffect(() => {
    if (updateUserNamesContext) {
      updateUserNames({
        variables: {
          request: updateUserNamesContext.request
        }
      });
    }
  }, [updateUserNamesContext]);

  function setupContext(
    request: UpdateUserNamesRequest,
    callback: InlineTextEditCallback<User>
  ) {
    const context: UpdateUserNamesContext = {
      request,
      callback
    };
    setUpdateUserNamesContext(context);
  }

  const [
    deleteUser,
    { loading: deletingUser, error: deleteUserError, data: userDeleted }
  ] = useMutation<DeleteUserData, DeleteUserVars>(
    MembershipGraphQL.Mutation.DELETE_USER,
    {
      client: membershipApolloClient,
      update: (
        cache: DataProxy,
        { data }: ExecutionPatchResult<DeleteUserData>
      ) => {
        const users = cache.readQuery<AllUsersData>({
          query: MembershipGraphQL.Query.ALL_USERS
        });
        if (!users) {
          return;
        }
        const usersClone = cloneDeep(users.allUsers);
        const deletedUserIndex = usersClone.findIndex(
          (u) => u.username === data.deleteUser
        );
        if (deletedUserIndex < 0) {
          return;
        }
        usersClone.splice(deletedUserIndex, 1);
        cache.writeQuery<AllUsersData>({
          query: MembershipGraphQL.Query.ALL_USERS,
          data: { allUsers: usersClone }
        });
      }
    }
  );

  if (deletingUser && deleteErrorAcknowledged) {
    setDeleteErrorAcknowledged(false);
  }

  function acknowledgeDeleteError() {
    setDeleteErrorAcknowledged(true);
  }

  const [
    _verifyOrUnverifyEmailManually,
    { loading: updating, error: updatingError, data: verifyOrUnverifyEmailData }
  ] = useMutation<
    VerifyOrUnverifyEmailManaullyData,
    VerifyOrUnverifyEmailManaullyVars
  >(MembershipGraphQL.Mutation.VERIFY_OR_UNVERIFY_EMAIL_MANUALLY, {
    client: membershipApolloClient,
    update: (
      cache: DataProxy,
      { data }: ExecutionPatchResult<VerifyOrUnverifyEmailManaullyData>
    ) => {
      let users: AllUsersData;
      try {
        users = cache.readQuery<AllUsersData>({
          query: MembershipGraphQL.Query.ALL_USERS
        });
      } catch (error) {
        // NOP - cache miss ok
      }
      if (!users?.allUsers?.length || !data) {
        return;
      }
      const usersClone = cloneDeep(users.allUsers);
      const user = usersClone.find(
        (u) => u.userkey === data.verifyOrUnverifyEmailManually.userkey
      );
      const { verifiedemail } = data.verifyOrUnverifyEmailManually;
      user.verifiedemail = verifiedemail;
      cache.writeQuery<AllUsersData>({
        query: MembershipGraphQL.Query.ALL_USERS,
        data: { allUsers: usersClone }
      });
    }
  });

  const verifyOrUnverifyEmailManually = async (
    userkey: number,
    email?: string
  ) => {
    _verifyOrUnverifyEmailManually({
      variables: {
        userkey,
        email
      }
    });
  };

  return {
    updateUserNames: (request: UpdateUserNamesRequest) =>
      updateUserNames({
        variables: {
          request
        }
      }),
    updateUserNamesCalled,
    updateUserNamesLoading,
    updateUserNamesError:
      updateUserNamesError && !updateUserNamesErrorAcknowledged,
    acknowledgeUpdateUserNamesError,
    updateUserNamesData: updateUserNamesData?.updateUserNames,
    setupContext,
    deleteUser,
    deletingUser,
    deleteUserError: deleteUserError && !deleteErrorAcknowledged,
    acknowledgeDeleteError,
    userDeleted,
    verifyOrUnverifyEmailManually,
    updating,
    updatingError,
    verifyOrUnverifyData:
      verifyOrUnverifyEmailData?.verifyOrUnverifyEmailManually
  };
}

export default useUserEdit;
